import React from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { refreshAccessToken, setAccessToken } from "@/lib/auth";
// Initialize i18n once at app entry
import "@/lib/i18n";
// Initialize prefetching for better performance
import { initPrefetching } from "@/lib/prefetch";
import { ErrorBoundary } from './components/ErrorBoundary';

// Global error handler to detect React hook corruption and auto-refresh
(function setupGlobalErrorHandler() {
  const REFRESH_KEY = '__react_error_refresh_attempted';
  
  // Handle unhandled errors
  window.addEventListener('error', (event) => {
    const error = event.error || event.message;
    const errorMessage = typeof error === 'string' ? error : error?.message || '';
    const errorStack = error?.stack || '';

    // Check if this is a React hook corruption error
    const isReactHookError = 
      errorMessage.includes('Invalid hook call') ||
      errorMessage.includes('dispatcher is null') ||
      errorMessage.includes("can't access property") ||
      errorStack.includes('useContext') ||
      errorStack.includes('useTranslation') ||
      (errorStack.includes('React') && errorMessage.includes('hook'));

    if (isReactHookError && !sessionStorage.getItem(REFRESH_KEY)) {
      sessionStorage.setItem(REFRESH_KEY, 'true');
      console.warn('React state corruption detected. Refreshing page...');
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    const errorMessage = error?.message || String(error || '');
    const errorStack = error?.stack || '';

    // Check if this is a React hook corruption error
    const isReactHookError = 
      errorMessage.includes('Invalid hook call') ||
      errorMessage.includes('dispatcher is null') ||
      errorMessage.includes("can't access property") ||
      errorStack.includes('useContext') ||
      errorStack.includes('useTranslation');

    if (isReactHookError && !sessionStorage.getItem(REFRESH_KEY)) {
      sessionStorage.setItem(REFRESH_KEY, 'true');
      console.warn('React state corruption detected in promise rejection. Refreshing page...');
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  });

  // Clear the refresh flag on successful page load (after a delay to ensure app mounted)
  window.addEventListener('load', () => {
    setTimeout(() => {
      sessionStorage.removeItem(REFRESH_KEY);
    }, 2000);
  });
})();

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      // keep list queries fresh for a short period so navigating between pages
      // doesn't immediately refetch the same data (reduces duplicate requests)
      staleTime: 1000 * 60 * 5, // 5 minutes
      // avoid refetching on mount if we already have cached data
      refetchOnMount: false,
    },
  },
});

// Await token rehydration before mounting the app so initial queries won't
// trigger a duplicate refresh call while the app also calls refresh.
(async () => {
  try {
    const token = await refreshAccessToken();
    if (token) setAccessToken(token);
  } catch (e) {
    // ignore
  }

  // Initialize prefetching for background loading
  initPrefetching();

  const Wrapper: any = import.meta.env.DEV ? React.Fragment : React.StrictMode;

  createRoot(document.getElementById("root")!).render(
    <Wrapper>
      <ErrorBoundary>
        <QueryClientProvider client={qc}>
          <App />
        </QueryClientProvider>
      </ErrorBoundary>
    </Wrapper>
  );
})();
